import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";

import { oids } from "@/configs/Settings";
import { useMessagingTemplatesForSend } from "src-next/modules/crm-clinical/messaging";
import { mapRawTemplatesToMessagingTemplates } from "src-next/modules/crm-clinical/messaging/hooks/useMessagingTemplatesForSend";
import { getOrFetchMessageTemplates } from "src-next/modules/crm-clinical/messageTemplates/services/messageTemplatesCache";
import type {
  MessagingPatient,
  MessagingTemplate,
} from "src-next/modules/crm-clinical/messaging/types";
import { isVetApp } from "../../messaging/helpers/isVetApp";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import type { MessageTemplateQuestionnaireOption } from "src-next/modules/crm-clinical/messageTemplates/components/MessageTemplateFormDialog";

import {
  mapCrmPatientSummaryToMessagingPatient,
  taskPatientReferenceFromCrmPatient,
} from "../helpers/mapCrmPatientSummaryToMessagingPatient";
import { resolveMessagingTemplateByCode } from "../helpers/resolveMessagingTemplateByCode";
import { resolveMessagingTutorsFromCrmPets } from "../helpers/resolveMessagingTutorsFromCrmPets";
import type { OpenCommunicationsWhatsAppSendParams } from "../types/sendMessage";
import type { CrmPatientSummary } from "../types";

export interface UseCommunicationsWhatsAppSendDialogParams {
  organizationId?: string;
  practitionerId?: string;
  onMessageSent?: () => void;
  /** Alinhado ao board CRM — recarrega franquia no dialog após envio/compra. */
  franchiseRefreshSignal?: number;
  hasActiveMetaEndpoint?: boolean;
}

interface SendDialogPayload {
  patient: MessagingPatient | null;
  initialSelectedPatients?: MessagingPatient[];
  staticPatientListForSelection?: MessagingPatient[];
  template: MessagingTemplate | null;
  taskPatientReference: string | null;
}

const EMPTY_PAYLOAD: SendDialogPayload = {
  patient: null,
  template: null,
  taskPatientReference: null,
};

/**
 * Orquestra estado e dados para um único `SendWhatsAppMessageDialog` nas listas CRM.
 */
export function useCommunicationsWhatsAppSendDialog({
  organizationId,
  practitionerId,
  onMessageSent,
  franchiseRefreshSignal = 0,
  hasActiveMetaEndpoint,
}: UseCommunicationsWhatsAppSendDialogParams) {
  const [open, setOpen] = useState(false);
  const [purchasePackagesOnly, setPurchasePackagesOnly] = useState(false);
  const [payload, setPayload] = useState<SendDialogPayload>(EMPTY_PAYLOAD);

  const { templates: messagingTemplatesForSend, wabaIdRank1 } =
    useMessagingTemplatesForSend({
      organizationId,
      // Lista e questionários só quando o dialog abre. Cache quente não gera rede.
      enabled: open,
    });

  const {
    questionnaires,
    isLoading: isQuestionnairesLoading,
    errorMessage: questionnairesErrorMessage,
  } = useQuestionnaireManagement({
    organizationId: organizationId
      ? `${oids.cnpj}-${organizationId}`
      : undefined,
    enabled: open,
  });

  const questionnaireOptions = useMemo<MessageTemplateQuestionnaireOption[]>(
    () =>
      questionnaires
        .filter((item) => Boolean(item.id))
        .map((item) => ({
          id: item.id as string,
          title: item.title,
        })),
    [questionnaires],
  );

  const openSendMessage = useCallback(
    async ({
      patients = [],
      templateCode,
      purchasePackagesOnly: openPurchasePackagesOnly,
    }: OpenCommunicationsWhatsAppSendParams) => {
      if (openPurchasePackagesOnly) {
        setPayload(EMPTY_PAYLOAD);
        setPurchasePackagesOnly(true);
        setOpen(true);
        return;
      }

      if (patients.length === 0) return;

      setPurchasePackagesOnly(false);

      const wabaId = (wabaIdRank1 ?? "").trim();
      let templatesForResolve = messagingTemplatesForSend;
      if (wabaId) {
        try {
          templatesForResolve = mapRawTemplatesToMessagingTemplates(
            await getOrFetchMessageTemplates(wabaId),
            organizationId,
          );
        } catch {
          // O dialog abre mesmo assim; o hook com `enabled: open` mostra o erro.
          templatesForResolve = messagingTemplatesForSend;
        }
      }

      const template = resolveMessagingTemplateByCode(
        templatesForResolve,
        templateCode,
      );

      /**
       * Vet mode: a seleção/envio real é para o Tutor (RelatedPerson), por CPF.
       * Logo, projetamos os tutores para `MessagingPatient` e abrimos o dialog já
       * com `initialSelectedPatients` (sem depender de lookup interno do dialog).
       */
      if (isVetApp()) {
        const tutors = await resolveMessagingTutorsFromCrmPets(patients);
        if (tutors.length === 1) {
          setPayload({
            patient: tutors[0],
            initialSelectedPatients: undefined,
            staticPatientListForSelection: undefined,
            template,
            taskPatientReference: null,
          });
        } else {
          setPayload({
            patient: null,
            initialSelectedPatients: tutors,
            staticPatientListForSelection: tutors,
            template,
            taskPatientReference: null,
          });
        }
        setOpen(true);
        return;
      }

      const messagingPatients = patients.map((p) =>
        mapCrmPatientSummaryToMessagingPatient(p),
      );

      if (messagingPatients.length === 1) {
        const crmPatient = patients[0];
        setPayload({
          patient: messagingPatients[0],
          initialSelectedPatients: undefined,
          staticPatientListForSelection: undefined,
          template,
          taskPatientReference: taskPatientReferenceFromCrmPatient(crmPatient),
        });
      } else {
        setPayload({
          patient: null,
          initialSelectedPatients: messagingPatients,
          staticPatientListForSelection: messagingPatients,
          template,
          taskPatientReference: null,
        });
      }
      setOpen(true);
    },
    [messagingTemplatesForSend, organizationId, wabaIdRank1],
  );

  const closeSendMessage = useCallback(() => {
    setOpen(false);
    setPayload(EMPTY_PAYLOAD);
    setPurchasePackagesOnly(false);
  }, []);

  return {
    openSendMessage,
    closeSendMessage,
    sendDialogProps: {
      open,
      onClose: closeSendMessage,
      organizationId,
      practitionerId,
      onMessageSent,
      appointment: { id: "ad-hoc", startAtIso: dayjs().toISOString() },
      patient: purchasePackagesOnly ? null : payload.patient,
      template: purchasePackagesOnly ? null : payload.template,
      templates: purchasePackagesOnly ? [] : messagingTemplatesForSend,
      questionnaires: purchasePackagesOnly ? [] : questionnaireOptions,
      isQuestionnairesLoading: purchasePackagesOnly ? false : isQuestionnairesLoading,
      questionnairesErrorMessage: purchasePackagesOnly
        ? null
        : questionnairesErrorMessage,
      staticPatientListForSelection: purchasePackagesOnly
        ? undefined
        : payload.staticPatientListForSelection,
      initialSelectedPatients: purchasePackagesOnly
        ? undefined
        : payload.initialSelectedPatients,
      taskPatientReference: purchasePackagesOnly
        ? null
        : payload.taskPatientReference,
      enforceMessageFranchiseGate: !purchasePackagesOnly,
      franchiseRefreshSignal,
      hasActiveMetaEndpoint,
      purchasePackagesOnly,
    },
  };
}
